import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '@/services/apiClient';
import Button from '@/components/ui/Button';
import styles from './ImageUpload.module.css';

/**
 * Upload de imagem reutilizável (estilo FLUX). Mostra o preview atual e um
 * botão "Enviar" que abre o seletor de arquivos, faz o upload e devolve a URL.
 *
 * @param {string}   value     URL atual (controlado).
 * @param {Function} onChange  Recebe a nova URL após o upload.
 * @param {string}   folder    Pasta de destino (avatars|covers|exercises|...).
 * @param {string}   label     Rótulo opcional acima do controle.
 * @param {boolean}  round     Preview circular (avatar).
 * @param {string}   accept    Tipos aceitos (default imagem; use "video/mp4" pra vídeo).
 */
export default function ImageUpload({ value, onChange, folder = 'misc', label, round = false, accept = 'image/*' }) {
  const isVideo = accept.startsWith('video');
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reenviar o mesmo arquivo
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const url = await api.upload(file, folder);
      onChange?.(url);
    } catch (err) {
      setError(err.message || t('imageUpload.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrap}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.row}>
        <div className={`${styles.preview} ${round ? styles.round : ''}`}>
          {value ? (
            isVideo ? (
              <video src={value} muted playsInline preload="metadata" />
            ) : (
              <img src={value} alt={label || t('imageUpload.imageAlt')} />
            )
          ) : (
            <span className={styles.placeholder}>—</span>
          )}
        </div>
        <Button type="button" variant="outline" icon={Upload} loading={loading} onClick={pick}>
          {t('imageUpload.send')}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onFile}
          style={{ display: 'none' }}
        />
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}
